import type { ReactNode } from 'react'
import { cn } from './cn'
import { comboLabel } from '@/lib/hooks'

export interface KbdProps {
  /** A combo ("mod+k", "g o", "?") rendered as platform-aware keycaps (⌘K on macOS, Ctrl+K elsewhere). */
  combo?: string
  children?: ReactNode
  tone?: 'default' | 'inverse'
  className?: string
}

/** Keycap(s): `<Kbd combo="mod+k" />` or `<Kbd>Esc</Kbd>`. Sequences render "then" between steps. */
export function Kbd({ combo, children, tone = 'default', className }: KbdProps) {
  const cls = cn(
    'inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] px-1 font-sans text-[11px] font-medium leading-none tabular',
    tone === 'default' ? 'border border-border bg-surface-sunken text-text-muted shadow-[inset_0_-1px_0_var(--border)]' : 'bg-white/15 text-white/85',
    className,
  )
  if (!combo) return <kbd className={cls}>{children}</kbd>
  const steps = comboLabel(combo)
  return (
    <span className="inline-flex items-center gap-1" aria-label={combo}>
      {steps.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className={cn('text-[10px]', tone === 'default' ? 'text-text-subtle' : 'text-white/60')}>then</span>}
          <kbd className={cls}>{s}</kbd>
        </span>
      ))}
    </span>
  )
}
